import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, finalize, map, switchMap } from 'rxjs/operators';
import { IAppRequest } from '@app/app.interface';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { DesignerFilterService } from '@app/modules/designer-filter/designer-filter.service';
import { AppService } from '@app/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ENotifyTypes, NotifierService } from '@services/notifier.service';
import { IFilter } from '@app/modules/form-filter/interfaces/IFilter';

/**
 * Сервис для управления сохраненными поисками
 */
@Injectable()
export class FormFilterSavedSearchesService {
    readonly #dialogConfirm: AppDialogService = inject(AppDialogService);
    readonly #filterService: DesignerFilterService = inject(DesignerFilterService);
    readonly #appService: AppService = inject(AppService);
    readonly #spinner: NgxSpinnerService = inject(NgxSpinnerService);
    readonly #notifier: NotifierService = inject(NotifierService);

    /**
     * Получить поток с количеством записей filterRecords
     */
    public get filterRecordsLength$(): Observable<number> {
        return this.#extendFilterRecords$.pipe(map((arr) => arr.length));
    }

    /**
     * Получить текущее количество записей filterRecords
     */
    public get filterRecordsLength() {
        return this.#extendFilterRecords$.value.length;
    }

    /**
     * Получить поток значений filterRecords
     */
    public get filterRecords$() {
        return this.#extendFilterRecords$.asObservable();
    }

    /**
     * Получить текущее значение filterRecords
     */
    public get filterRecords() {
        return this.#extendFilterRecords$.value;
    }

    /**
     * Передать массив записей в фильтр
     * @param arr
     */
    public set filterRecords(arr: any[]) {
        if (Array.isArray(arr)) {
            this.#extendFilterRecords$.next(arr);
        }
    }

    public get selectedFilterId$() {
        return this.#selectedFilterId$.asObservable();
    }

    public get selectedFilterId() {
        return this.#selectedFilterId$.value;
    }

    public set selectedFilterId(id: string) {
        this.#selectedFilterId$.next(id);
    }

    /**
     * Записи в фильтре
     * @private
     */
    readonly #extendFilterRecords$: BehaviorSubject<any[]> = new BehaviorSubject([]);

    /**
     * ID текущего поиска
     * @private
     */
    readonly #selectedFilterId$: BehaviorSubject<string> = new BehaviorSubject(null);

    /**
     * Выбор сохраненного поиска
     * @param filter
     */
    public select(filter: IFilter) {
        if (filter) {
            this.#selectedFilterId$.next(String(filter.id));
        } else {
            this.#selectedFilterId$.next(null);
        }
    }

    /**
     * Удаление сохраненного поиска
     * @param event событие клика
     * @param filter сохраненный поиск
     */
    deleteFilterRecord(event: PointerEvent, filterRecord: IFilter) {
        event.stopPropagation();
        this.#dialogConfirm
            .confirm(
                this.#appService.language_dictionary.system_attention,
                this.#appService.language_dictionary.files_are_you_sure_want_del,
            )
            .pipe(
                filter((res) => res),
                switchMap(() => {
                    this.#spinner.show();
                    return this.#filterService.remove({ id: filterRecord.id });
                }),
                finalize(() => this.#spinner.hide()),
            )
            .subscribe({
                next: (result: IAppRequest) => {
                    if (result && !result.is_error) {
                        this.#notifier.notify(
                            ENotifyTypes.success,
                            this.#appService.language_dictionary.system_data_successfully_deleted,
                        );
                        this.filterRecords = this.filterRecords.filter((record) => record.id !== filterRecord.id);
                    } else {
                        this.#notifier.notify(ENotifyTypes.error, result.message);
                    }
                },
                error: () => {
                    this.#notifier.notify(
                        ENotifyTypes.error,
                        this.#appService.language_dictionary.system_no_connection_with_system,
                    );
                },
            });
    }
}
